import { StatusMap } from '@/components/data-table/constants';
import { Icons } from '@/components/icons';
import {
  defaultBooleanFilter,
  defaultDateFilter,
  defaultEnumFilter,
  defaultStringFilter,
  OPERATORS,
  RELATION_FILTER,
  type Filter,
} from '@/components/table-filters/constants';
import { ApartmentContractConfig, TenantConfig } from '@pigello/pigello-matrix';
import baseRevenueIncreaseFilters from './base-revenue-increase-filters';

export const SUB_LEASE_ID = 'sub_lease_active';
export const GUARANTOR_ID = 'guarantor_active';
export const STATUS_FILTER_ID = 'contract_status';

const STATUS_FILTERS: Filter['enumChoices'] = [
  {
    title: StatusMap.contract[0].text,
    filters: [
      {
        operator: '__in',
        value: '0',
      },
    ],
    badgeVariant: StatusMap.contract[0].color,
  },
  {
    title: StatusMap.contract[1].text,
    filters: [
      {
        operator: '__in',
        value: '1',
      },
    ],
    badgeVariant: StatusMap.contract[1].color,
  },
  {
    title: StatusMap.contract[2].text,
    filters: [
      {
        operator: '__in',
        value: '2',
      },
    ],
    badgeVariant: StatusMap.contract[2].color,
  },
  {
    title: StatusMap.contract[3].text,
    filters: [{ operator: '__in', value: '3' }],
    badgeVariant: StatusMap.contract[3].color,
  },
  {
    title: StatusMap.contract[4].text,
    filters: [{ operator: '__in', value: '4' }],
    badgeVariant: StatusMap.contract[4].color,
  },
  {
    title: StatusMap.contract[5].text,
    filters: [{ operator: '__in', value: '5' }],
    badgeVariant: StatusMap.contract[5].color,
  },
  {
    title: StatusMap.contract[6].text,
    filters: [{ operator: '__in', value: '6' }],
    badgeVariant: StatusMap.contract[6].color,
  },
  {
    title: StatusMap.contract[7].text,
    filters: [{ operator: '__in', value: '7,8' }],
    badgeVariant: StatusMap.contract[7].color,
  },

  {
    title: StatusMap.contract[9].text,
    filters: [{ operator: '__in', value: '9' }],
    badgeVariant: StatusMap.contract[9].color,
  },
  {
    title: StatusMap.contract[10].text,
    filters: [{ operator: '__in', value: '10' }],
    badgeVariant: StatusMap.contract[10].color,
  },
  {
    title: StatusMap.contract[11].text,
    filters: [{ operator: '__in', value: '11' }],
    badgeVariant: StatusMap.contract[11].color,
  },
  {
    title: StatusMap.contract[12].text,
    filters: [{ operator: '__in', value: '12' }],
    badgeVariant: StatusMap.contract[12].color,
  },
  {
    id: SUB_LEASE_ID,
    title: 'Andrahandsuthyrt',
    filters: [{ operator: 'noop', value: 'sub_lease_active' }],
    badgeVariant: 'cyan',
    overrideExternalFieldName: 'custom_contract_filter',
  },
  {
    id: GUARANTOR_ID,
    title: 'Borgensman',
    filters: [{ operator: 'noop', value: 'guarantor_active' }],
    badgeVariant: 'cyan',
    overrideExternalFieldName: 'custom_contract_filter',
  },
];

const baseContractFilters: Filter[] = [
  {
    id: STATUS_FILTER_ID,
    ...defaultEnumFilter,
    title: ApartmentContractConfig.fields.status.verboseName,
    externalFieldName: ApartmentContractConfig.fields.status.externalFieldName,
    enumChoices: STATUS_FILTERS,
  },
  {
    kind: RELATION_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    title: ApartmentContractConfig.fields.tenant.verboseName,
    externalFieldName: 'tenants',
    relationModelName: TenantConfig.modelName,
    icon: Icons.tenant,
    id: 'contract_tenant',
  },
  {
    ...defaultStringFilter,
    title: ApartmentContractConfig.fields.idNumber.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.idNumber.externalFieldName,
    icon: Icons.id,
  },
  {
    ...defaultStringFilter,
    title: ApartmentContractConfig.fields.customId.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.customId.externalFieldName,
    icon: Icons.id,
  },
  {
    ...defaultDateFilter,
    title: ApartmentContractConfig.fields.startDate.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.startDate.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: ApartmentContractConfig.fields.initialEndDate.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.initialEndDate.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: ApartmentContractConfig.fields.lifetimeEndDate.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.lifetimeEndDate.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: ApartmentContractConfig.fields.cancellationTime.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.cancellationTime.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateFilter,
    title: ApartmentContractConfig.fields.invoicingPeriodFrom.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingPeriodFrom.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title: ApartmentContractConfig.fields.invoicingMarking.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingMarking.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title:
      ApartmentContractConfig.fields.invoicingCustomerReference.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingCustomerReference
        .externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: ApartmentContractConfig.fields.invoicingDebtBlocked.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingDebtBlocked.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: ApartmentContractConfig.fields.invoicingReminderBlocked.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingReminderBlocked.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: ApartmentContractConfig.fields.invoiceMergeIndexInView.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoiceMergeIndexInView.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title:
      ApartmentContractConfig.fields.invoiceMergeNonBaseRentInView.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoiceMergeNonBaseRentInView
        .externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title:
      ApartmentContractConfig.fields.invoicingAutomaticCreationPaused
        .verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingAutomaticCreationPaused
        .externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: ApartmentContractConfig.fields.invoicingAttemptAutogiro.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingAttemptAutogiro.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: ApartmentContractConfig.fields.signedTime.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.signedTime.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultBooleanFilter,
    title: ApartmentContractConfig.fields.invoicingIsInternal.verboseName,
    externalFieldName:
      ApartmentContractConfig.fields.invoicingIsInternal.externalFieldName,
  },
  ...baseRevenueIncreaseFilters,
];

export default baseContractFilters;
